/**
 * Referral program types.
 *
 * Referral Code Program:
 *   - 1 month free per referral (up to 10 per calendar year) for monthly subscribers
 *   - 10 referrals in a year = free annual plan for following year (annual subscribers)
 *
 * Rewards are applied via Chargebee promotional credits (not coupons) so
 * they stack naturally — each referral adds an independent credit that
 * auto-applies to the next invoice.
 *
 * (Future) Social Media Post Program:
 *   - 1 month free per positive social media post (max 1 per quarter)
 */

/** Referral code format: TRYB-XXXXXX (6 alphanumeric chars) */
export interface ReferralCode {
  code: string;
  householdId: string;
  householdName: string;
  createdAt: string;
}

/** A single completed referral */
export interface ReferralRecord {
  referrerHouseholdId: string;
  referredHouseholdId: string;
  referredEmail: string;
  referralCode: string;
  referredAt: string;
  rewardApplied: boolean;
  rewardAppliedAt?: string;
  /** Calendar year this referral counts toward */
  year: number;
}

/** Response from GET /v1/households/{householdId}/referral */
export interface ReferralInfoResponse {
  referralCode: string;
  referralsThisYear: number;
  referralCap: number; // 10
  rewardsEarned: number;
  referrals: {
    referredEmail: string;
    referredAt: string;
    rewardApplied: boolean;
  }[];
  /** Future: social media program info */
  socialMediaProgram: {
    enabled: boolean;
    description: string;
  };
}

/** Request to validate a referral code (used on SubscribePage) */
export interface ValidateReferralRequest {
  referralCode: string;
}

/** Response from POST /v1/referral/validate */
export interface ValidateReferralResponse {
  valid: boolean;
  referrerName?: string;
  message?: string;
}

/** Referral program constants */
export const REFERRAL_PROGRAM = {
  /** Max referrals per calendar year */
  MAX_REFERRALS_PER_YEAR: 10,
  /** Referral code prefix */
  CODE_PREFIX: 'TRYB',
  /** Referral code character length (after prefix) */
  CODE_LENGTH: 6,
  /** Credit amount per referral in cents — $3.99 (same for all plan types) */
  CREDIT_AMOUNT_CENTS: 399,
} as const;
